import type { SiteComposite } from '../../types/portfolioComposite';
import {
  DECISION_BAND_RANK,
  bandForScore,
  normalizeBandConfig,
  toDecisionScore,
  type DecisionBandConfig,
} from './decisionBands';
import { PILLARS, isAdvisoryPillar, type PillarId } from './pillarRegistry';
import { VERDICT_WITHHELD_LABEL } from './decisionBands';

type DerivedBand = 'go' | 'conditional' | 'conditional-high' | 'no-go';

export interface DerivedSiteVerdict {
  /** Computed band from L1+L2; null when composite unavailable OR data inadequate. */
  band: DerivedBand | null;
  bandLabel: string;
  // R-130-4 — keep a null label field to make stale persisted labels impossible.
  label: null;
  bandColor: 'green' | 'amber' | 'orange' | 'red' | 'gray';
  composite: number | null;
  confidenceTag: 'V' | 'R' | 'C' | null;
  dataPenalty: number;
  computedAt: string | null;
  /** True when the Exec Summary narrative claim disagrees with the derived band. */
  narrativeDivergence: boolean;
  narrativeClaim: string | null;
  // ── Adequacy gate (R-VERDICT-ADEQUACY) ────────────────────────────────────
  // A site only earns a COLOURED GO/CONDITIONAL/NO-GO band when its underlying
  // data is adequate enough to stand behind a recommendation. When inadequate,
  // band is forced null + bandColor 'gray' so no surface shows a premature
  // (and often misleadingly red) final verdict. Consumers can render the reason
  // and coverage instead of a score.
  /** False when the composite is missing OR the data behind it is inadequate. */
  dataAdequate: boolean;
  /**
   * Human-readable reason a band is withheld — an adequacy gap (`dataAdequate`
   * false) or a DECISION HOLD (`dataAdequate` true, `composite.decisionHold`
   * set — P1.4c). Null when a band is asserted.
   */
  adequacyReason: string | null;
  /** Fraction (0–1) of scoring pillars with real (non-missing) data; null when no composite. */
  coverageRatio: number | null;
}

/**
 * Minimum fraction of scoring pillars that must carry real (non-missing) data
 * before a coloured band is shown. Below this, a verdict is WITHHELD (gray
 * "Insufficient data") regardless of the composite value.
 *
 * Part C SP4 (S4) — the composite AGGREGATE is now HARD_MISSING
 * (PORTFOLIO_CONFIG.MISSING_AGGREGATE_MODE), so a sparse site's composite is
 * NO LONGER dragged toward 0 by worst-case fills — it reflects only the
 * MEASURED pillars. This coverage gate is therefore no longer compensating for
 * imputed-worst drag; its PURPOSE is now narrower but still essential: a band
 * derived from too FEW measured pillars (e.g. a 2-of-9-pillar site reading GO on
 * two strong pillars) is not trustworthy enough to assert. The gate withholds
 * the verdict until the majority of decision pillars are measured.
 *
 * 0.5 = "the majority of decision pillars must be measured, not imputed."
 * FLAGGED FOR REVIEW: this is a product threshold. Raise it to be stricter
 * (more sites read as "in progress"); lower it to surface verdicts earlier.
 */
export const MIN_PILLAR_COVERAGE = 0.5;

/**
 * Minimum fraction of decision WEIGHT that must be measured before a coloured
 * band is shown — the second half of the adequacy floor, and the half that
 * matches how the composite is actually computed.
 *
 * `MIN_PILLAR_COVERAGE` counts pillars, which silently treats them as
 * interchangeable. They are not: `power` carries 0.21 of the decision and
 * `land` 0.05. A site measuring the five CHEAPEST decision pillars clears the
 * count floor at 5/9 = 0.556 while only ~0.38 of the decision weight has any
 * evidence behind it, and the composite it publishes is a weighted mean over
 * that thin slice. In practice the binding consequence is the right one: a
 * data-centre verdict can no longer be published while `power` — the single
 * heaviest pillar, and the question the product exists to answer — is
 * unmeasured.
 *
 * This gate is ADDITIVE and strictly one-directional: it sits alongside the
 * count floor rather than replacing it, so it can only ever withhold MORE
 * verdicts, never publish one the count floor withholds today. That matters
 * because loosening adequacy is how a thin site starts asserting a band.
 *
 * 0.65 = "clearly more than the bare majority of the decision, by weight."
 *
 * HISTORY. Reviewed and confirmed at 0.60 on 2026-09-03, rejecting 0.67 as
 * putting too many reasonably-evidenced sites into "in progress" and 0.55 as
 * changing little. That review is superseded, on a deliberate product decision
 * (2026-09-04), because the arithmetic underneath it moved.
 *
 * WHY IT MOVED. Stage 5.2 added `feasibility` to `PILLARS.power.sourceReports`
 * — correctly, because Stage 5.1 gave BTM Feasibility the grid-screening
 * evidence. But power carries 0.21, the heaviest weight in the frame, so a site
 * with ONE feasibility report jumped from 0.33 to 0.54 measured weight. Against
 * the 0.90 non-advisory frame that is EXACTLY 0.60, and the gate reads
 * `weightCoverage < MIN_DECISION_WEIGHT_COVERAGE` — so at 0.60 a single report
 * published a coloured GO/NO-GO band. Two further facts made that untenable:
 *
 *   - `evidenceQuality` is set `missing: false` UNCONDITIONALLY on every
 *     computed composite (compositeScorer.ts), so its 0.03 is always in the
 *     numerator. This is not an edge case reachable by unlucky sites; it is
 *     every feasibility-only site, every time.
 *   - `power-availability` adds ZERO coverage beyond feasibility — power is the
 *     only pillar it feeds. So "feasibility alone" and "feasibility + the
 *     Generation & Impact report" are the SAME 0.60 point, and no threshold can
 *     separate them. The choice was binary: one report publishes a verdict, or
 *     a second substantive domain is required.
 *
 * 0.65 sits in a real gap in the achievable ladder — the next step up is a
 * second domain report (environmental / water / supply-chain / sentiment, each
 * 0.08-0.10) at ~0.689 — so it is robust to floating-point equality in a way
 * 0.60 was not.
 *
 * ACCEPTED CONSEQUENCE, stated because it is customer-visible: this RETRACTS
 * published verdicts. Every pillar set landing in [0.60, 0.65) moves to
 * "insufficient data", which includes feasibility-only and feasibility +
 * Generation & Impact sites, and reaches as far as 7-of-9 pillar coverage for a
 * site that measured seven cheap pillars but neither power nor financial.
 * Withholding is the safe direction — it declines to assert rather than
 * asserting something false — but it is a retraction and must be announced,
 * not shipped quietly.
 */
export const MIN_DECISION_WEIGHT_COVERAGE = 0.65;

/**
 * Evaluate whether a site composite's underlying data is adequate to stand
 * behind a coloured GO/NO-GO band — the single policy used by every verdict
 * surface (dots, pills, bars, portfolio snapshot, risk matrix).
 *
 * Policy (conservative — suppresses verdicts built on absent coverage or an
 * unresolved material conflict, NOT legitimately data-backed scores carrying
 * advisory QA caveats, so a real conditional/no-go still shows). A verdict is
 * withheld when ANY of:
 *   - there is no source report at all, OR
 *   - there is at least one UNRESOLVED HIGH-severity evidence conflict (SED D1.1)
 *     — a material provider-vs-document disagreement the user has not resolved;
 *     the verdict is withheld until it is reconciled, OR
 *   - pillar coverage is below MIN_PILLAR_COVERAGE — a band derived from too few
 *     MEASURED pillars (the composite aggregate is HARD_MISSING, so it reflects
 *     only present pillars) is "not enough data yet" to stand behind, not a real
 *     verdict, regardless of whether the few measured pillars score high or low.
 *
 * coverageRatio is always reported (for an "N% coverage" display) as the
 * fraction of scoring pillars that have real, non-missing data.
 */
/**
 * Legacy withheld reason for a 'C' (coarse) data-confidence tag.
 *
 * New adequacy decisions do not withhold solely for C: when report coverage is
 * sufficient and no unresolved high conflict exists, the computed band remains
 * visible and C is shown as a data-trust caveat. Keep this export so Path-to-GO
 * can still interpret older persisted reasons without brittle string literals.
 */
export const COARSE_CONFIDENCE_WITHHELD_REASON =
  'Data confidence too low (coarse) for a final recommendation';

export function decisionPillarCoverage(composite: SiteComposite): {
  measuredPillarCount: number;
  applicablePillarCount: number;
  coverageRatio: number;
  /**
   * Fraction of decision WEIGHT measured, over the same pillar frame as
   * `coverageRatio`. Reported separately rather than folded into
   * `coverageRatio` because that value is rendered as "N% pillar coverage" on
   * a dozen surfaces; silently redefining what those percentages mean would be
   * a worse bug than the one this fixes.
   *
   * `null` means the frame carries NO registry weight at all — every pillar id
   * present is outside the current registry (legacy composites, fixtures). That
   * is UNKNOWN coverage, not zero coverage, and the adequacy gate must not read
   * it as a failure: an unrecognised pillar set is exactly the input on which
   * fail-closed produces a false "insufficient data".
   */
  weightCoverageRatio: number | null;
} {
  // Coverage answers "how much of the FIXED decision frame has evidence". The
  // denominator must therefore stay on the static non-advisory set and must NOT
  // shrink because the user has not done the work yet — that absence is exactly
  // the signal this ratio exists to report. Filtering it by applicability moved
  // a 4-measured site from 4/9 = 0.444 (below MIN_PILLAR_COVERAGE, verdict
  // withheld as "Insufficient data") to 4/7 = 0.571 (coloured GO/NO-GO band
  // published) on identical evidence, with only ~0.39 of decision weight
  // actually measured. The reverse was also reachable, retracting verdicts
  // customers can see today.
  const pillarScores = Object.values(composite.pillars ?? {}).filter(
    (p) => !isAdvisoryPillar(p.pillarId),
  );
  const measuredPillarCount = pillarScores.filter((p) => !p.missing).length;
  // Weights come from the REGISTRY, never from the persisted pillar row: a
  // stale or hand-authored `weight` on a composite must not be able to buy
  // adequacy. An id outside the current registry weighs 0 on both sides.
  const weightFor = (p: { pillarId: PillarId }) => PILLARS[p.pillarId]?.weight ?? 0;
  const frameWeight = pillarScores.reduce((sum, p) => sum + weightFor(p), 0);
  const measuredWeight = pillarScores
    .filter((p) => !p.missing)
    .reduce((sum, p) => sum + weightFor(p), 0);
  return {
    measuredPillarCount,
    applicablePillarCount: pillarScores.length,
    coverageRatio:
      pillarScores.length > 0 ? measuredPillarCount / pillarScores.length : 0,
    weightCoverageRatio: frameWeight > 0 ? measuredWeight / frameWeight : null,
  };
}

export function evaluateSiteDataAdequacy(composite: SiteComposite): {
  adequate: boolean;
  reason: string | null;
  coverageRatio: number;
} {
  // CONNECTIVITY-ADVISORY — advisory pillars are excluded from BOTH sides of
  // the coverage ratio: they are not decision-material, so an unmeasured one
  // must not count against coverage, and a measured one must not count toward
  // it (a site cannot buy adequacy with an informational pillar).
  const coverage = decisionPillarCoverage(composite);
  const coverageRatio = coverage.coverageRatio;
  // Same frame as `decisionPillarCoverage` above. This list is used ONLY as a
  // "do we know the pillar set at all" guard, but filtering it by applicability
  // five lines from a coverage ratio computed over the static frame reads as a
  // second, smaller denominator. One frame, visible in one function.
  const pillarScores = Object.values(composite.pillars ?? {}).filter(
    (p) => !isAdvisoryPillar(p.pillarId),
  );

  const reportCount = composite.sourceReportIds?.length ?? 0;
  if (reportCount === 0) {
    return { adequate: false, reason: 'No source reports yet', coverageRatio };
  }
  // SED D1.1 — withhold the verdict when there is an UNRESOLVED HIGH-severity
  // evidence conflict (a material provider-vs-document disagreement the user has
  // neither resolved nor had auto-resolved). This trigger is LOAD-BEARING and
  // sits ALONGSIDE the tag/coverage gates — NOT replacing them: the additive
  // penalty alone (rate 0.10) lands a single HIGH conflict in the 'R' tag, not
  // 'C', so without this branch a single unresolved HIGH conflict would still
  // surface a coloured GO/NO-GO verdict. The strongest trust signal must gate
  // the verdict directly. Absent field ⇒ `?? 0` ⇒ never fires (additive).
  //
  // This remains a hard gate even when confidence is C. A named unresolved
  // material conflict is different from advisory provenance/reconciliation QA
  // findings and must prevent a recommendation until it is resolved.
  if ((composite.dataConfidenceBreakdown?.unresolvedHighConflicts ?? 0) > 0) {
    return {
      adequate: false,
      reason: 'Unresolved high-severity evidence conflict(s) — verdict withheld',
      coverageRatio,
    };
  }
  // Only apply the coverage floor when we actually know the pillar set. An empty
  // pillar map (coverageRatio 0 with zero pillars) is treated as "unknown
  // coverage" and falls through to the report/tag checks rather than
  // hard-failing here — avoids false "insufficient" on fixtures/edge inputs.
  //
  // Thin coverage is the dominant cause of the unmeasured-pillar penalty, so a
  // sparse site often also carries C. Coverage is the precise actionable gate;
  // the confidence tag remains visible separately as a trust signal.
  if (pillarScores.length > 0 && coverageRatio < MIN_PILLAR_COVERAGE) {
    return {
      adequate: false,
      reason: `Only ${Math.round(coverageRatio * 100)}% of decision pillars have data`,
      coverageRatio,
    };
  }
  // Second half of the floor: enough pillars is not the same as enough DECISION.
  // The count gate above passes a site measuring the five cheapest pillars
  // (5/9 = 0.556) on ~0.38 of the decision weight — and the band it publishes
  // is a weighted mean, so it is dominated by pillars that were never measured.
  // Ordered AFTER the count gate deliberately: when both fail, the count reason
  // is the more actionable thing to tell a user ("run more analyses"), while
  // this one is the more precise ("run the analyses that matter").
  const weightCoverage = coverage.weightCoverageRatio;
  if (weightCoverage != null && weightCoverage < MIN_DECISION_WEIGHT_COVERAGE) {
    return {
      adequate: false,
      reason: `Only ${Math.round(weightCoverage * 100)}% of decision weight is measured`,
      coverageRatio,
    };
  }
  // Do not turn advisory QA/provenance warnings into "Insufficient data" once
  // the majority of decision pillars is measured. The computed band is still
  // the decision result; confidence C remains explicit in the Orbit detail rail
  // and other trust surfaces so users can see and address the caveat.
  return { adequate: true, reason: null, coverageRatio };
}

/**
 * P1.4c — is this composite's band withheld for ANY reason: the adequacy gate
 * (evidence) or a decision hold (undeclared power strategy)? The portfolio
 * surfaces that used to call `evaluateSiteDataAdequacy` directly missed the
 * hold and rendered a colour the Decision stage withheld; every band-gating
 * consumer that does not go through `deriveSiteVerdict` reads this instead.
 */
export function siteVerdictWithheld(composite: SiteComposite): boolean {
  return !evaluateSiteDataAdequacy(composite).adequate || composite.decisionHold != null;
}

export interface DeriveSiteVerdictOptions {
  // R-130-4 — admin Sites uses decision-scale composites directly.
  scoreMode?: 'benefit' | 'decision';
  // R-130-4 — optionally force live-config dependency (render "—" while loading).
  requireBandConfig?: boolean;
}

function normalizeBandId(
  id: 'go' | 'conditional' | 'conditionalHigh' | 'noGo',
): DerivedBand {
  switch (id) {
    case 'go':
      return 'go';
    case 'conditional':
      return 'conditional';
    case 'conditionalHigh':
      return 'conditional-high';
    case 'noGo':
      return 'no-go';
  }
}

function configBandIdForDerivedBand(band: DerivedBand): 'go' | 'conditional' | 'conditionalHigh' | 'noGo' {
  switch (band) {
    case 'go':
      return 'go';
    case 'conditional':
      return 'conditional';
    case 'conditional-high':
      return 'conditionalHigh';
    case 'no-go':
      return 'noGo';
  }
}

export function resolveBandLabelFromConfig(
  band: DerivedBand | null,
  bandConfig?: DecisionBandConfig,
): string | null {
  if (!band || !bandConfig?.bands?.length) return null;
  const configBandId = configBandIdForDerivedBand(band);
  return bandConfig.bands.find((entry) => entry.id === configBandId)?.label ?? null;
}

function normalizeNarrativeClaim(status: string | null): DerivedBand | null {
  if (!status) return null;
  const normalized = status.trim().toLowerCase().replace(/[_\s]+/g, '-');
  if (!normalized) return null;

  if (normalized === 'go' || normalized === 'proceed' || normalized === 'green') return 'go';
  if (
    normalized === 'conditional'
    || normalized === 'proceed-with-conditions'
    || normalized === 'caution'
    || normalized === 'amber'
  ) {
    return 'conditional';
  }
  if (
    normalized === 'conditional-high'
    || normalized === 'conditional-high-risk'
    || normalized === 'high-conditional'
    || normalized === 'orange'
  ) {
    return 'conditional-high';
  }
  if (
    normalized === 'no-go'
    || normalized === 'nogo'
    || normalized === 'do-not-proceed'
    || normalized === 'stop'
    || normalized === 'blocked'
    || normalized === 'red'
  ) {
    return 'no-go';
  }
  return null;
}

export function deriveSiteVerdict(
  composite: SiteComposite | null,
  executiveSummaryStatus: string | null,
  bandConfig?: DecisionBandConfig,
  options?: DeriveSiteVerdictOptions,
): DerivedSiteVerdict {
  const requireBandConfig = options?.requireBandConfig ?? false;
  if (!composite || (requireBandConfig && !bandConfig?.bands?.length)) {
    return {
      band: null,
      bandLabel: '—',
      label: null,
      bandColor: 'gray',
      composite: null,
      confidenceTag: null,
      dataPenalty: 0,
      computedAt: null,
      narrativeDivergence: false,
      narrativeClaim: executiveSummaryStatus,
      dataAdequate: false,
      adequacyReason: 'Not yet computed',
      coverageRatio: null,
    };
  }

  // Adequacy gate: withhold a coloured band when the data behind the composite
  // is not yet trustworthy enough to stand behind a recommendation. This is the
  // single source of truth for "is this site ready for a verdict?" — every
  // surface (dot/pill/bar/snapshot/risk-matrix) inherits it via band == null.
  const adequacy = evaluateSiteDataAdequacy(composite);
  if (!adequacy.adequate) {
    return {
      band: null,
      bandLabel: VERDICT_WITHHELD_LABEL,
      label: null,
      bandColor: 'gray',
      composite: composite.composite,
      confidenceTag: composite.confidenceTag,
      dataPenalty: composite.dataCompletenessPenalty,
      computedAt: composite.lastComputedAt,
      // Suppress narrative divergence when we are not asserting a band at all.
      narrativeDivergence: false,
      narrativeClaim: executiveSummaryStatus,
      dataAdequate: false,
      adequacyReason: adequacy.reason,
      coverageRatio: adequacy.coverageRatio,
    };
  }

  // P1.4c (W6) — a decision hold withholds the band AFTER the adequacy gate:
  // the data is adequate, the decision authority is missing. Same
  // `band === null` contract every surface already honours, with the rollup's
  // own PATH 1b label so the portfolio and the Decision Guide say one thing.
  if (composite.decisionHold) {
    return {
      band: null,
      bandLabel: composite.decisionHold.label,
      label: null,
      bandColor: 'gray',
      composite: composite.composite,
      confidenceTag: composite.confidenceTag,
      dataPenalty: composite.dataCompletenessPenalty,
      computedAt: composite.lastComputedAt,
      narrativeDivergence: false,
      narrativeClaim: executiveSummaryStatus,
      dataAdequate: true,
      adequacyReason: composite.decisionHold.reason,
      coverageRatio: adequacy.coverageRatio,
    };
  }

  const score = options?.scoreMode === 'decision'
    ? composite.composite
    : toDecisionScore(composite.composite);
  const scoredBand = bandForScore(score, bandConfig);

  /**
   * SOURCE-FLOOR PARITY — the persisted band can be WORSE than the score.
   *
   * `computeSiteComposite` caps `decisionBand` when a mandatory source report
   * reads red, but deliberately leaves `composite.composite` at the uncapped
   * value. Deriving the band from that score alone therefore threw the cap
   * away on every surface that renders a verdict from the composite: the
   * portfolio map plotted a green pin, the snapshot a green "Proceed" pill and
   * the risk matrix a green dot, on the very row whose band FILTER — which
   * reads `decisionBand` — classified it as noGo. That is the "green GO pill
   * in the portfolio while the Decision Guide reads NO-GO" leak, still open on
   * exactly the surfaces it was supposed to be closed on.
   *
   * Take the WORSE of the two, never the better: a persisted band may be
   * stale, so it is allowed to lower a verdict but never to raise one.
   */
  const persistedBand = composite.decisionBand;
  const capped =
    persistedBand != null
    && DECISION_BAND_RANK[persistedBand] != null
    && DECISION_BAND_RANK[persistedBand] < DECISION_BAND_RANK[scoredBand.id]
      ? normalizeBandConfig(bandConfig).bands.find(b => b.id === persistedBand)
      : undefined;
  const derivedBand = capped ?? scoredBand;
  const normalizedDerivedBand = normalizeBandId(derivedBand.id);
  const narrativeBand = normalizeNarrativeClaim(executiveSummaryStatus);
  // R-130-4 — tone mapping for the admin surfaces:
  // no-go=red, conditional-high=amber, conditional=soft-amber(orange), go=green.
  const normalizedBandColor = normalizedDerivedBand === 'conditional-high'
    ? 'amber'
    : normalizedDerivedBand === 'conditional'
      ? 'orange'
      : derivedBand.color;

  return {
    band: normalizedDerivedBand,
    bandLabel: resolveBandLabelFromConfig(normalizedDerivedBand, bandConfig) || derivedBand.label,
    label: null,
    bandColor: normalizedBandColor,
    composite: composite.composite,
    confidenceTag: composite.confidenceTag,
    dataPenalty: composite.dataCompletenessPenalty,
    computedAt: composite.lastComputedAt,
    narrativeDivergence: narrativeBand != null && narrativeBand !== normalizedDerivedBand,
    narrativeClaim: executiveSummaryStatus,
    dataAdequate: true,
    adequacyReason: null,
    coverageRatio: adequacy.coverageRatio,
  };
}
